import React, { useEffect, useState } from "react";
import PostForm from "../components/PostForm";
import PostFeed from "../components/PostFeed";
import AuthModal from "../components/AuthModal";
import { fetchUser, logout } from "../utils/auth";
import usePageMeta from "../utils/usePageMeta";
import AdSlot from "../components/AdSlot";

const Community = () => {
  usePageMeta({
    title: "Community",
    description:
      "Share numbers, strategies and thoughts with other California lottery players. Posts are written by visitors, not by LottoNest.",
    path: "/community",
  });

  const [posts, setPosts] = useState([]);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const fetchPosts = async () => {
    try {
      const res = await fetch("/api/posts");
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch posts:", err);
    }
  };

  useEffect(() => {
    fetchPosts();
    // The session survives a reload even though the cookie is unreadable here.
    fetchUser().then(setUser);
  }, []);

  const handleLogout = async () => {
    await logout();
    setUser(null);
  };

  const handleLoginSuccess = (loggedInUser) => {
    setUser(loggedInUser);
    setShowAuthModal(false);
  };

  const likePost = async (postId) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      const res = await fetch("/api/posts", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId, action: "like" }),
      });

      if (!res.ok) {
        if (res.status === 401) setShowAuthModal(true);
        return;
      }

      const updatedPost = await res.json();

      // Update the posts state with the updated post
      setPosts((prev) =>
        prev.map((p) => (p._id === updatedPost._id ? updatedPost : p))
      );
    } catch (err) {
      console.error("Error liking post:", err);
    }
  };

  const addComment = async (postId, text) => {
    if (!user || !text) return;

    try {
      const res = await fetch("/api/posts", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId, action: "comment", text }),
      });

      if (!res.ok) {
        if (res.status === 401) setShowAuthModal(true);
        return;
      }

      const updatedPost = await res.json();

      setPosts((prev) =>
        prev.map((p) => (p._id === updatedPost._id ? updatedPost : p))
      );
    } catch (err) {
      console.error("Error adding comment:", err);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      const res = await fetch("/api/posts", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId }),
      });
      if (!res.ok) {
        if (res.status === 401) setShowAuthModal(true);
        return;
      }
      setPosts((prev) => prev.filter((p) => p._id !== postId));
    } catch (err) {
      console.error("Error deleting post:", err);
    }
  };

  const deleteComment = async (postId, commentId) => {
    try {
      const res = await fetch("/api/posts", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId, action: "deleteComment", commentId }),
      });
      if (!res.ok) {
        if (res.status === 401) setShowAuthModal(true);
        return;
      }
      const updatedPost = await res.json();
      setPosts((prev) =>
        prev.map((p) => (p._id === updatedPost._id ? updatedPost : p))
      );
    } catch (err) {
      console.error("Error deleting comment:", err);
    }
  };

  return (
    <div className="community-page">
      <h1>Community Feed</h1>
      <p>Share your numbers, like, and comment!</p>

      {user ? (
        <p className="signed-in-as">
          Signed in as <strong>@{user.username}</strong>{" "}
          <button onClick={handleLogout}>Log out</button>
        </p>
      ) : (
        <button onClick={() => setShowAuthModal(true)}>
          Sign Up / Login to Post
        </button>
      )}

      {user && <PostForm onPostCreated={fetchPosts} />}

      <AdSlot slot={process.env.REACT_APP_ADSENSE_SLOT_FEED} />

      <PostFeed
        posts={posts}
        user={user}
        onLike={likePost}
        onComment={addComment}
        onDeletePost={deletePost}
        onDeleteComment={deleteComment}
      />

      {showAuthModal && <AuthModal onLoginSuccess={handleLoginSuccess} />}
    </div>
  );
};

export default Community;
